import * as dbDirectories from 'serviceWorker/db/directories/directories';
import * as dbDirectoriesService from 'serviceWorker/db/directories/directoriesService';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { directoryEntityToModel } from 'serviceWorker/repository/directory/mappings';
import { DirectoryModel } from 'shared/domain/directory/directoryModel';
import { getUsersSynchronized } from 'serviceWorker/services/users/getUsers';
import { UserEntity } from 'shared/domain/user/types/entity';
import { awaitEntities } from 'serviceWorker/services/factories/awaitEntities';
import { waitForMessage } from 'serviceWorker/services/factories/waitForMessage';
import { DirectoriesBatchCreatedModel } from 'shared/domain/directory/directoryModel';
import db from 'serviceWorker/db';
import { directoryContract, type BreadcrumbItem } from 'shared/domain/directory/directoryContract';
import { documentationFilteringCacheManager } from 'serviceWorker/repository/documentation/documentationFilteringCacheManager/index';
import {
  makeErrorPublisher,
  makeEventPublisher,
  makeLoadedEventPublisher,
  readCollectionPayload,
} from 'serviceWorker/broadcasts/publishers';

export const broadcastAllDirectories = makeLoadedEventPublisher(
  directoryContract,
  DomainMessagesTypes.allDirectories,
  () => readCollectionPayload(dbDirectoriesService, getAllDirectories)
);

const emitCreatedDirectory = makeEventPublisher(directoryContract, DomainMessagesTypes.createdEntity);

export const broadcastUploadedDirectory = makeEventPublisher(directoryContract, DomainMessagesTypes.uploadedEntity);

const emitDirectoryMoved = makeEventPublisher(directoryContract, DomainMessagesTypes.documentationMoved);

const emitDirectoryDeleted = makeEventPublisher(directoryContract, DomainMessagesTypes.documentationDeleted);

const emitDirectoryRestored = makeEventPublisher(directoryContract, DomainMessagesTypes.documentationRestored);

export const broadcastDirectoryEdited = makeEventPublisher(directoryContract, DomainMessagesTypes.updatedEntity);

export function broadcastCreatedDirectory(
  payload: unknown,
  uniqueId?: string
): void {
  documentationFilteringCacheManager.invalidateAllDocumentationFilteringCaches();
  emitCreatedDirectory(payload, uniqueId);
}

export function broadcastDirectoryMoved(
  payload: unknown,
  uniqueId?: string
): void {
  documentationFilteringCacheManager.invalidateAllDocumentationFilteringCaches();
  emitDirectoryMoved(payload, uniqueId);
}

export function broadcastDirectoryDeleted(
  payload: unknown,
  uniqueId?: string
): void {
  documentationFilteringCacheManager.invalidateAllDocumentationFilteringCaches();
  emitDirectoryDeleted(payload, uniqueId);
}

export function broadcastDirectoryRestored(
  payload: unknown,
  uniqueId?: string
): void {
  documentationFilteringCacheManager.invalidateAllDocumentationFilteringCaches();
  emitDirectoryRestored(payload, uniqueId);
}

const emitDirectoriesBatchCreated = makeEventPublisher(
  directoryContract,
  DomainMessagesTypes.directoriesBatchCreated
);

const publishDirectoryError = makeErrorPublisher(directoryContract);

export function broadcastDirectoriesBatchCreated(
  payload: DirectoriesBatchCreatedModel,
  uniqueId: string
): void {
  documentationFilteringCacheManager.invalidateAllDocumentationFilteringCaches();
  emitDirectoriesBatchCreated(payload, uniqueId);
}

export function broadcastDirectoriesBatchCreateFailed(
  error: unknown,
  uniqueId: string
): void {
  publishDirectoryError(DomainMessagesTypes.directoriesBatchCreated, error, { uniqueId });
}

// Imports break when we try to put this function in services.
/** Subdirectories of `value`, each marked whether it is empty. */
export async function getDirectoriesByQuery(
  value: string
): Promise<DirectoryModel[]> {
  const [dirs, users] = await Promise.all([
    dbDirectories.getByQuery({ parentId: value }),
    getUsersSynchronized(),
  ]);

  const directoryIds = dirs
    .map((dir) => dir._id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);

  if (directoryIds.length === 0) {
    return dirs.map((dir) => directoryEntityToModel(dir, users));
  }

  const [subdirectories, files] = await Promise.all([
    db.directories
      .where('parentId')
      .anyOf(directoryIds)
      .and((entity) => entity.deleted === 0)
      .toArray(),
    db.documentations
      .where('parentId')
      .anyOf(directoryIds)
      .and((entity) => entity.deleted === 0)
      .toArray(),
  ]);

  const nonEmptyDirectoryIds = new Set<string>();

  subdirectories.forEach((subdirectory) => {
    if (subdirectory.parentId) {
      nonEmptyDirectoryIds.add(subdirectory.parentId);
    }
  });

  files.forEach((file) => {
    if (file.parentId) {
      nonEmptyDirectoryIds.add(file.parentId);
    }
  });

  return dirs.map((dir) => {
    const model = directoryEntityToModel(dir, users);

    if (!dir._id) {
      return model;
    }

    return {
      ...model,
      isEmptyDirectory: !nonEmptyDirectoryIds.has(dir._id),
    };
  });
}

/** Directories from the root down to `directoryId`. */
export async function getDirectoryBreadcrumbs(
  directoryId: string
): Promise<BreadcrumbItem[]> {
  const users = await getUsersSynchronized();
  const roots = await findRoots(users, [], directoryId);
  // Roots are read by remote id, so every one of them has `_id`.
  return roots.flatMap(({ _id, name }) => (_id ? [{ _id, name }] : []));
}

export async function areDirectoriesDownloaded(): Promise<boolean> {
  const serviceData = await dbDirectoriesService.get();
  return Boolean(serviceData?.isDownloaded);
}

/** Every directory, once directories are downloaded. */
export async function getAllDirectoryModels(): Promise<DirectoryModel[]> {
  await awaitEntities(
    dbDirectoriesService,
    waitForMessage(
      directoryContract,
      DomainMessagesTypes.allDirectories
    )
  );
  return getAllDirectories();
}

async function getAllDirectories(): Promise<DirectoryModel[]> {
  const [directories, users] = await Promise.all([
    dbDirectories.get(),
    getUsersSynchronized(),
  ]);

  return directories.map((entity) => directoryEntityToModel(entity, users));
}

async function findRoots(
  users: UserEntity[],
  results: DirectoryModel[],
  value: string
): Promise<DirectoryModel[]> {
  if (!value) return [];
  const directory = await dbDirectories.getOneByRemote(value);
  if (!directory) {
    return results;
  }
  results.push(directoryEntityToModel(directory, users));
  if (directory.parentId) {
    return findRoots(users, results, directory.parentId);
  }
  return results.reverse();
}
